import type { SessionInfo } from '@garden-platform/shared';
import { CheckCircle, Clock, Globe, Loader2, Monitor, Shield, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { getSessions, invalidateSession, invalidateUserSessions } from '../../services/auth';

interface SessionWithUser extends SessionInfo {
  user?: { id: string; email: string; name: string | null };
}

export default function Sessions() {
  const [sessions, setSessions] = useState<SessionWithUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'valid' | 'invalid'>('valid');

  const loadSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = filter === 'all' ? undefined : (filter as 'valid' | 'invalid');
      const result = await getSessions(1, 50, status);
      if (result.success && result.data) {
        setSessions(result.data.data as SessionWithUser[]);
      }
    } catch (_err) {
      setError('Failed to load sessions');
    }
    setIsLoading(false);
  }, [filter]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleInvalidateSession = async (sessionId: string) => {
    try {
      const result = await invalidateSession(sessionId);
      if (result.success) {
        loadSessions();
      }
    } catch (_err) {
      setError('Failed to invalidate session');
    }
  };

  const handleInvalidateAllUserSessions = async (userId: string) => {
    if (!confirm('Are you sure you want to invalidate all sessions for this user?')) return;

    try {
      const result = await invalidateUserSessions(userId);
      if (result.success) {
        loadSessions();
      }
    } catch (_err) {
      setError('Failed to invalidate sessions');
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleString();
  };

  const parseUserAgent = (ua: string | null) => {
    if (!ua) return 'Unknown device';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown browser';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Sessions</h1>
        <p className="text-muted-foreground">
          Monitor and manage active sessions across your organization
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'valid' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('valid')}
        >
          <CheckCircle className="h-4 w-4 mr-1" />
          Active
        </Button>
        <Button
          variant={filter === 'invalid' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('invalid')}
        >
          <XCircle className="h-4 w-4 mr-1" />
          Invalid
        </Button>
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          All
        </Button>
      </div>

      {/* Sessions list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Session List
          </CardTitle>
          <CardDescription>
            {sessions.length} session{sessions.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No sessions found</p>
          ) : (
            <div className="space-y-3">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className={`p-4 border rounded-lg ${
                    session.isValid && new Date(session.expiresAt) > new Date()
                      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/50'
                      : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {session.isValid && new Date(session.expiresAt) > new Date() ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="font-medium">
                          {session.user?.name || session.user?.email || 'Unknown user'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          {session.ipAddress || 'Unknown IP'}
                        </div>
                        <div className="flex items-center gap-1">
                          <Monitor className="h-3 w-3" />
                          {parseUserAgent(session.userAgent)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Created: {formatDate(session.createdAt)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Expires: {formatDate(session.expiresAt)}
                        </div>
                      </div>
                    </div>

                    {session.isValid && new Date(session.expiresAt) > new Date() && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleInvalidateSession(session.id)}
                        >
                          Revoke
                        </Button>
                        {session.userId && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleInvalidateAllUserSessions(session.userId)}
                          >
                            Revoke All
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
